import { useEffect, useMemo, useRef } from "react";
import { useApi } from "@backstage/core-plugin-api";
import { configApiRef } from "@backstage/core-plugin-api";
import {
  NavigationConfig,
  NavigationItem,
  NavigationSettings,
} from "../types/navigation";
import { NavigationDefaults, FALLBACK_CONFIG } from "../utils/navigationDefaults";
import { getIconForEntity } from "../utils/icons";
import { normalizeCatalogSchema } from "../utils/catalogSchema";
import { parseNavigationConfig } from "../utils/navigationConfigParser";
import { useCatalogSchema } from "./useCatalogSchema";
import { createDebugLogger } from "../utils/debug";

const logger = createDebugLogger("useNavigationConfig");

export function useNavigationConfig(): NavigationConfig {
  const configApi = useApi(configApiRef);
  const catalogSchema = useCatalogSchema();
  const previousConfigRef = useRef<NavigationConfig>();
  const schemaLoadingRef = useRef(false);

  // Track if we're still loading catalog schema
  useEffect(() => {
    schemaLoadingRef.current = catalogSchema === undefined;
  }, [catalogSchema]);

  return useMemo(() => {
    logger.log("Computing navigation configuration");
    logger.time("navigation-config");

    try {
      // Try to load from app config
      const navConfig = configApi.getOptionalConfig("app.navigation");

      if (navConfig) {
        logger.log("Found navigation configuration in app-config");
        const config = parseNavigationConfig(navConfig);
        logger.log("Parsed config:", config);

        // Check if settings are properly configured
        const settings = config.settings || {
          showOther: false, // Default to false - don't show Other group unless explicitly enabled
        };
        logger.log("Navigation settings:", settings);

        // Apply showOther setting - adds "Other" group with all unmatched entities
        if (settings.showOther === true && catalogSchema) {
          // Ensure typesByKind is a Map
          const validSchema = normalizeCatalogSchema(catalogSchema);

          logger.log("Adding Other group for unconfigured entities");

          // When showOther is true with custom groups, add an "Other" group
          // for all entities not matching custom filters
          if (config.groups && config.groups.length > 0) {
            // Collect all entity types from the catalog that aren't already configured
            const configuredFilters = new Set<string>();

            // Build a set of what's already configured
            config.groups.forEach((group) => {
              group.items.forEach((item) => {
                if (item.filter && typeof item.filter === "object") {
                  const key = `${item.filter.kind || ""}-${
                    item.filter.type || ""
                  }`;
                  configuredFilters.add(key);
                  logger.log(`Added configured filter: ${key}`, item.filter);
                }
              });
            });
            logger.log("Configured filters:", Array.from(configuredFilters));

            const otherItems: NavigationItem[] = [];

            // Add items for each kind/type that isn't already configured
            validSchema.kinds.forEach((kind) => {
              const types = validSchema.typesByKind.get(kind);
              logger.log(
                `Processing kind: ${kind}, types:`,
                types ? Array.from(types) : "none",
              );

              if (types && types.size > 0) {
                // Add items for each type not already configured
                types.forEach((type) => {
                  const key = `${kind}-${type}`;
                  if (!configuredFilters.has(key)) {
                    logger.log(`Adding unconfigured: ${key}`);
                    otherItems.push({
                      title: `${kind} - ${type}`,
                      filter: { kind, type },
                      icon: getIconForEntity(kind, type),
                    });
                  } else {
                    logger.log(`Skipping configured: ${key}`);
                  }
                });
              } else {
                // Add item for kind without types if not configured
                const key = `${kind}-`;
                if (!configuredFilters.has(key)) {
                  logger.log(`Adding unconfigured kind: ${key}`);
                  otherItems.push({
                    title: kind,
                    filter: { kind },
                    icon: getIconForEntity(kind),
                  });
                } else {
                  logger.log(`Skipping configured kind: ${key}`);
                }
              }
            });

            // Add "Other" group with unconfigured items
            logger.log(
              `Found ${otherItems.length} unconfigured items for Other group`,
            );
            if (otherItems.length > 0) {
              config.groups.push({
                id: "other",
                title: "Other",
                icon: "folder",
                priority: 1000, // Very low priority to appear last
                items: otherItems,
              });
              logger.log("Added Other group with items:", otherItems);
            } else {
              logger.log("No unconfigured items found, not adding Other group");
            }
          } else {
            // No custom groups, generate default navigation
            const defaultGroups = NavigationDefaults.generateDefaultGroups(
              validSchema,
              true, // Always show uncategorized when no custom config
            );
            config.groups = defaultGroups;
          }
        } else if (settings.showOther === true && !catalogSchema) {
          // Catalog schema not loaded yet but autodiscover is on
          logger.log("Catalog schema not loaded yet, waiting...");
          if (previousConfigRef.current) {
            logger.timeEnd("navigation-config");
            return previousConfigRef.current;
          }
        } else {
          // showOther is false - use config as-is
          logger.log("Other group disabled, using configured groups only");
        }

        // Store settings in config
        config.settings = settings;

        // Ensure groups exist (use configured groups or empty array)
        if (!config.groups) {
          logger.warn(
            "No navigation groups configured and autoDiscover is disabled",
          );
          config.groups = [];
        }

        logger.log("Final navigation config:", config);
        previousConfigRef.current = config;
        logger.timeEnd("navigation-config");
        return config;
      }

      // Generate dynamic defaults if catalog schema is available
      if (catalogSchema && catalogSchema.kinds.length > 0) {
        logger.log("No custom config found, generating dynamic defaults");

        // Ensure typesByKind is a Map
        const validSchema = normalizeCatalogSchema(catalogSchema);

        logger.log("Available kinds:", validSchema.kinds);
        logger.log(
          "Types by kind:",
          Array.from(validSchema.typesByKind.entries()),
        );

        // Default settings when no config is provided
        const defaultSettings: NavigationSettings = {
          showOther: false, // Default to false
        };

        const defaultGroups = NavigationDefaults.generateDefaultGroups(
          validSchema,
          true, // Always show uncategorized in default mode
        );

        logger.log("Generated groups:", defaultGroups);

        const config: NavigationConfig = {
          groups: defaultGroups,
          settings: defaultSettings,
        };

        previousConfigRef.current = config;
        logger.timeEnd("navigation-config");
        return config;
      }

      // If catalog schema not yet loaded, return a basic fallback
      // with common entity types based on what we see in the screenshot
      if (!catalogSchema || catalogSchema.kinds.length === 0) {
        logger.log("Catalog schema not available, using basic fallback");

        const basicFallback: NavigationConfig = {
          groups: [
            {
              id: "services",
              title: "Services",
              icon: "layers",
              priority: 10,
              items: [
                {
                  title: "Services",
                  filter: { kind: "Component", type: "service" },
                  icon: "cpu",
                },
                {
                  title: "Websites",
                  filter: { kind: "Component", type: "website" },
                  icon: "globe",
                },
              ],
            },
            {
              id: "infrastructure",
              title: "Infrastructure",
              icon: "server",
              priority: 20,
              items: [
                {
                  title: "Resources",
                  filter: { kind: "Component", type: "resource" },
                  icon: "box",
                },
                {
                  title: "Patterns",
                  filter: { kind: "Component", type: "pattern" },
                  icon: "shapes",
                },
              ],
            },
            {
              id: "libraries",
              title: "Libraries",
              icon: "book",
              priority: 30,
              items: [
                {
                  title: "Libraries",
                  filter: { kind: "Component", type: "library" },
                  icon: "package",
                },
              ],
            },
            {
              id: "all",
              title: "All",
              icon: "folder",
              priority: 100,
              items: [
                {
                  title: "All Components",
                  filter: { kind: "Component" },
                  icon: "package",
                },
                {
                  title: "All Systems",
                  filter: { kind: "System" },
                  icon: "layers",
                },
                {
                  title: "All APIs",
                  filter: { kind: "API" },
                  icon: "api",
                },
              ],
            },
          ],
          settings: {
            showOther: false, // Default to false
          },
        };

        // If we're still loading and have a previous config, use it
        if (schemaLoadingRef.current && previousConfigRef.current) {
          logger.log("Catalog schema loading, using previous config");
          logger.timeEnd("navigation-config");
          return previousConfigRef.current;
        }

        previousConfigRef.current = basicFallback;
        logger.timeEnd("navigation-config");
        return basicFallback;
      }

      // This should not be reached, but TypeScript needs a return
      logger.timeEnd("navigation-config");
      return FALLBACK_CONFIG;
    } catch (error) {
      logger.error("Error loading navigation configuration:", error);
      logger.timeEnd("navigation-config");

      // Return previous config if available to prevent flicker
      if (previousConfigRef.current) {
        return previousConfigRef.current;
      }

      return FALLBACK_CONFIG;
    }
  }, [configApi, catalogSchema]);
}
